import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Typography, Alert, IconButton } from "@mui/material";
import { Tooltip } from "@mui/material";
import { SketchPicker, ColorResult } from "react-color";
import { useNavigate } from "react-router-dom";
import { FileInput } from "../../components/common/FileInput";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { TextField, DropdownField } from "../../components/common/FormFields";
import HubAvailableLanguagesPicker from "../../components/common/HubAvailableLanguagesPicker";
import FormGroup from "../../components/common/FormGroup";
import { uploadFileToS3Complete } from "../../api/s3";
import { createSingleTrackFolder } from "../../api/company";
import ButtonComponent from "../../components/ui/Button";
import { useGlobalState } from "../../context/useGlobalState";
import { useNotification } from "../../context/NotificationContext";
import Breadcrumb from "../../components/common/Breadcrumb";
import { generateHubCreateBreadcrumbs } from "../../utils/breadcrumbHelpers";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import ImagePreview from "../../components/images/ImagePreview";
import { useImageValidation } from "../../hooks/useImageValidation";
import hubStyles from "./Hub.module.css";

interface HubFormValues {
    name: string;
    originLanguageId: string;
    availableLanguagesTab: string[];
    logo: string | undefined;
    color: string;
}

const HubForm = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { values, setFieldValue, errors, touched } = useFormikContext<HubFormValues>();
    const { data, loading } = useGlobalState();
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const colorPickerRef = useRef<HTMLDivElement>(null);
    const imageValidation = useImageValidation({
        type: "hubLogo",
        imageUrl: values.logo || undefined,
    });

    const handleLogoFileSelected = useCallback(
        async (files: File | File[]) => {
            const file = Array.isArray(files) ? files[0] : files;
            if (file) {
                const token = localStorage.getItem("token");
                if (!token) {
                    console.error("No authentication token found");
                    return;
                }

                await imageValidation.validateImage(file);

                uploadFileToS3Complete(
                    {
                        file,
                        isPublic: true,
                        isFolder: false,
                    },
                    token,
                )
                    .then((result) => {
                        setFieldValue("logo", result);
                    })
                    .catch(console.error);
            }
        },
        // validateImage is stable; full imageValidation object not needed
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue, imageValidation.validateImage],
    );

    const handleColorChange = (selectedColor: ColorResult) => {
        setFieldValue("color", selectedColor.hex);
    };

    const handleRemoveLogo = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setFieldValue("logo", null);
    };

    const toggleColorPicker = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsColorPickerOpen((prev) => !prev);
    };

    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
                setIsColorPickerOpen(false);
            }
        };

        if (isColorPickerOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isColorPickerOpen]);

    if (loading) {
        return (
            <div className="form-loading-container">
                <div className={hubStyles.formLoadingSpinner}></div>
                <p className="form-loading-text">{t("loadingLanguages")}</p>
            </div>
        );
    }

    return (
        <Form>
            <div className={hubStyles.hubFormContainer}>
                <div className={hubStyles.hubSections}>
                    {/* Origin Language */}
                    <FormGroup
                        title={t("originLanguage")}
                        subtitle={t("originLanguageDescription")}
                        className={`${hubStyles.hubField} ${hubStyles.stepDescription}`}
                        classes={{
                            root: hubStyles.formGroup,
                            header: hubStyles.formGroupHeader,
                            title: hubStyles.formGroupTitleMedium,
                            subtitle: hubStyles.formGroupSubtitleMedium,
                        }}
                        required
                    >
                        <Field
                            name="originLanguageId"
                            component={DropdownField}
                            options={data.languagesAI}
                            placeholder={t("selectOriginLanguage")}
                            fullWidth
                            maxWidth="form"
                        />
                        {touched.originLanguageId && errors.originLanguageId && (
                            <Alert severity="error" className="form-error-alert">
                                {errors.originLanguageId}
                            </Alert>
                        )}
                    </FormGroup>

                    {/* Name */}
                    <FormGroup
                        title={t("hubName")}
                        subtitle={t("hubNameDescription")}
                        className={`${hubStyles.hubField} ${hubStyles.stepDescription}`}
                        classes={{
                            root: hubStyles.formGroup,
                            header: hubStyles.formGroupHeader,
                            title: hubStyles.formGroupTitleMedium,
                            subtitle: hubStyles.formGroupSubtitleMedium,
                            tooltip: hubStyles.titleInfo,
                        }}
                        required
                        tooltip={t("doNotTranslateTooltip")}
                        tooltipPlacement="right"
                    >
                        <Field name="name" component={TextField} placeholder={t("enterHubName")} fullWidth />
                        {touched.name && errors.name && (
                            <Alert severity="error" className="form-error-alert">
                                {errors.name}
                            </Alert>
                        )}
                    </FormGroup>

                    {/* Available Languages */}
                    <FormGroup
                        title={t("availableLanguages")}
                        subtitle={t("availableLanguagesDescription")}
                        className={`${hubStyles.hubField} ${hubStyles.stepDescription}`}
                        classes={{
                            root: hubStyles.formGroup,
                            header: hubStyles.formGroupHeader,
                            title: hubStyles.formGroupTitleMedium,
                            subtitle: hubStyles.formGroupSubtitleMedium,
                        }}
                        required
                    >
                        <Field name="availableLanguagesTab">
                            {({ field, form }: any) => (
                                <HubAvailableLanguagesPicker
                                    selectedIds={field.value || []}
                                    onChange={(ids) => {
                                        void form.setFieldValue(field.name, ids, true);
                                        void form.setFieldTouched(field.name, true, false);
                                    }}
                                    options={data.languagesAI}
                                />
                            )}
                        </Field>
                        {touched.availableLanguagesTab && errors.availableLanguagesTab && (
                            <Alert severity="error" className="form-error-alert">
                                {errors.availableLanguagesTab}
                            </Alert>
                        )}
                    </FormGroup>

                    {/* Logo */}
                    <FormGroup
                        title={t("hubLogo")}
                        subtitle={t("hubLogoDescription")}
                        className={`${hubStyles.hubField} ${hubStyles.stepDescription}`}
                        classes={{
                            root: hubStyles.formGroup,
                            header: hubStyles.formGroupHeader,
                            title: hubStyles.formGroupTitleMedium,
                            subtitle: hubStyles.formGroupSubtitleMedium,
                        }}
                        required
                    >
                        <ImageSpecsBadges spec="hubLogo" warnings={imageValidation.warnings} />
                        <div className={`${hubStyles.fileUpload} form-file-upload-constrained`}>
                            <FileInput
                                onFileSelected={handleLogoFileSelected}
                                showButton={true}
                                buttonVariant="outline"
                                placeholder={t("dragAndDropLogoHereOrClickToSelect")}
                                acceptedFileTypes={IMAGE_FORMAT}
                                maxSize={IMAGE_MAX_SIZE_200_KB}
                                maxSizeLabel={t("imageMaxSize200kb")}
                            />
                        </div>

                        {values.logo && (
                            <div className={hubStyles.logoPreviewContainer}>
                                <div className={hubStyles.previewHeader}>
                                    <Typography variant="subtitle2" className={hubStyles.previewLabel}>
                                        {t("logoPreview")}:
                                    </Typography>
                                    <IconButton
                                        type="button"
                                        onClick={handleRemoveLogo}
                                        className={hubStyles.removeImageButton}
                                        size="small"
                                        title={t("removeLogo")}
                                    >
                                        ✕
                                    </IconButton>
                                </div>
                                <ImagePreview
                                    key={values.logo}
                                    id={values.logo}
                                    photo={values.logo}
                                    onDelete={() => setFieldValue("logo", null)}
                                />
                            </div>
                        )}
                    </FormGroup>

                    {/* Color */}
                    <FormGroup
                        title={t("hubColor")}
                        subtitle={t("hubColorDescription")}
                        className={`${hubStyles.hubField} ${hubStyles.stepDescription}`}
                        classes={{
                            root: hubStyles.formGroup,
                            header: hubStyles.formGroupHeader,
                            title: hubStyles.formGroupTitleMedium,
                            subtitle: hubStyles.formGroupSubtitleMedium,
                        }}
                    >
                        <div className={hubStyles.colorInputContainer} ref={colorPickerRef}>
                            <Field
                                name="color"
                                component={TextField}
                                placeholder={t("enterColorCode")}
                                className={hubStyles.colorInputField}
                            />
                            <div
                                className={hubStyles.colorPreview}
                                style={{ backgroundColor: values.color || "#000000" }}
                                onClick={toggleColorPicker}
                                title={t("clickToPickColor")}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleColorPicker(e as any);
                                    }
                                }}
                            >
                                <span>{values.color || "#000000"}</span>
                            </div>

                            {isColorPickerOpen && (
                                <div className="color-picker-popup">
                                    <div className="color-picker-header">
                                        <span className="color-picker-title">{t("pickColor")}</span>
                                        <button
                                            type="button"
                                            className="color-picker-close"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsColorPickerOpen(false);
                                            }}
                                            title={t("closeColorPicker")}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <SketchPicker
                                        color={values.color || "#000000"}
                                        onChange={handleColorChange}
                                        disableAlpha={true}
                                    />
                                </div>
                            )}
                        </div>

                        {touched.color && errors.color && (
                            <Alert severity="error" className="form-error-alert">
                                {errors.color}
                            </Alert>
                        )}
                    </FormGroup>
                </div>
            </div>
        </Form>
    );
};

const HubCreate: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [, setSubmitting] = useState(false);

    // Stable schema reference so Formik/Yup don’t re-run with a stale field state after each render
    const validationSchema = useMemo(
        () =>
            Yup.object({
                name: Yup.string().required(t("hubNameRequired")),
                originLanguageId: Yup.string().required(t("originLanguageRequired")),
                availableLanguagesTab: Yup.array()
                    .of(Yup.string().required())
                    .min(1, t("atLeastOneLanguageRequired"))
                    .required(t("atLeastOneLanguageRequired")),
                logo: Yup.string().required(t("logoRequired")),
                color: Yup.string().matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, t("invalidColorFormat")),
            }),
        [t],
    );

    // Initial values
    const initialValues: HubFormValues = {
        name: "",
        originLanguageId: "",
        availableLanguagesTab: [],
        logo: undefined,
        color: "#000000",
    };

    // Handle form submission
    const handleSubmit = async (values: HubFormValues, formikHelpers: FormikHelpers<HubFormValues>) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        setSubmitting(true);

        try {
            await createSingleTrackFolder({
                token,
                data: {
                    name: values.name,
                    originLanguageId: values.originLanguageId,
                    logo: values.logo,
                    color: values.color,
                    availableLanguagesTab: values.availableLanguagesTab,
                },
            });
            showNotification({
                message: t("hubCreatedSuccessfully"),
                type: "success",
            });
            setTimeout(() => {
                navigate("/hubs");
            }, 2000);
        } catch (error) {
            console.error("Error creating hub:", error);
            showNotification({
                message: t("errorCreatingHub"),
                type: "error",
            });
        } finally {
            setSubmitting(false);
            formikHelpers.setSubmitting(false);
        }
    };

    return (
        <div className={`${hubStyles.root} ${hubStyles.page}`}>
            <div className={`${hubStyles.pageHeader} ${hubStyles.pageHeaderCreate}`}>
                <Breadcrumb
                    classes={{
                        nav: hubStyles.breadcrumbNav,
                        separator: hubStyles.breadcrumbSeparator,
                        link: hubStyles.breadcrumbLink,
                        text: hubStyles.breadcrumbText,
                        active: hubStyles.breadcrumbActive,
                    }}
                    items={generateHubCreateBreadcrumbs({ t })}
                />
                <h1 className={hubStyles.pageTitle}>{t("createHub")}</h1>
                <p className={hubStyles.pageSub}>
                    {t("createHubPageSubtitle") ||
                        "Configurez la langue, l'identité et l'apparence de votre nouveau hub."}
                </p>
            </div>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validateOnMount
                validateOnChange
            >
                {({ isSubmitting, submitForm, errors }) => (
                    <>
                        <HubForm />

                        <div className={hubStyles.fixedButtonContainer}>
                            <ButtonComponent
                                variant="cancel"
                                shape="pill"
                                label={t("cancel")}
                                onClick={() => navigate("/hubs")}
                                disabled={isSubmitting}
                            />
                            <Tooltip
                                title={
                                    Object.keys(errors).length > 0
                                        ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                        : ""
                                }
                                arrow
                            >
                                <span>
                                    <ButtonComponent
                                        variant="primary"
                                        shape="pill"
                                        label={t("createHub")}
                                        onClick={submitForm}
                                        disabled={isSubmitting || Object.keys(errors).length > 0}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default HubCreate;
